import {useTheme} from "@emotion/react"
import {Box, styled, Typography} from "@mui/material"
import React, {useCallback, useContext} from "react"
import InfoIcon from "../../components/general/infoIcon"
import {getOptions} from "../../misc/widget-functions"
import {ActionContext} from "../../../src/context/ActionContextProvider"

export const widgetLabelPrototype = [
    {
        key: "color",
        type: "COLOR",
        label: "Textfarbe",
        size: 3,
    },
    {
        key: "bgcolor",
        type: "COLOR",
        label: "Hintergrundfarbe",
        size: 3,
    },
    {
        key: "textAlignment",
        type: "SELECT",
        label: "Text Positionierung",
        size: 3,
        options: [
            {
                value: "LEFT",
                label: "Links",
            },
            {
                value: "RIGHT",
                label: "Rechts",
            },
            {
                value: "CENTER",
                label: "Mittig",
            },
        ],
    },
    {
        key: "fontWeight",
        type: "SELECT",
        label: "Font weight",
        size: 3,
        options: [
            {
                value: "100",
                label: "100",
            },
            {
                value: "200",
                label: "200",
            },
            {
                value: "300",
                label: "300",
            },
            {
                value: "400",
                label: "400",
            },
            {
                value: "500",
                label: "500",
            },
            {
                value: "600",
                label: "600",
            },
            {
                value: "700",
                label: "700",
            },
            {
                value: "800",
                label: "800",
            },
            {
                value: "900",
                label: "900",
            },
        ],
    },
    {
        key: "wordBreak",
        type: "SELECT",
        label: "Word-break",
        size: 3,
        options: [
            {
                value: "normal",
                label: "normal",
            },
            {
                value: "break-all",
                label: "break-all",
            },
            {
                value: "keep-all",
                label: "keep-all",
            },
            {
                value: "auto-phrase",
                label: "auto-phrase",
            },
        ],
    },
    {
        key: "fontSize",
        type: "INTEGER",
        label: "Font Size in px",
        size: 3,
    },
    {
        key: "lineHeight",
        type: "INTEGER",
        label: "Line Height in px",
        size: 3,
    },
    {
        key: "infotext",
        type: "STRING",
        label: "infotext",
        size: 6,
    },
    {
        key: "textElement",
        type: "SELECTTREE",
        label: "Textbaustein",
        size: 6,
        options: "text",
    },
    {
        key: "padding",
        type: "STRING",
        label: "Padding",
        size: 3,
    },
]

const getTextFromTextElement = (textElement) => {
    const tenant = globalThis._env_.THEME
    return textElement.type === "innerNode"
        ? textElement.children.find((child) => child.label === tenant)?.text
        : textElement.text
}

const StyledLabel = styled(Typography, {shouldForwardProp: (props) => props !== "disabled"})(({theme, disabled}) => ({
    ...theme.STYLED_LABEL,
    color: disabled ? theme.STYLED_LABEL.colorDisabled : theme.STYLED_LABEL.color
}))

const LabelWidget = ({widget, value, error, disabled}) => {
    const {triggerAction} = useContext(ActionContext)
    const theme = useTheme()
    const {fontSize, lineHeight} = widget
    const errorState = error ? {color: theme.LABEL.errorColor} : {}
    const textElement = widget.textElement?.origin
    const displayValue = textElement
        ? getTextFromTextElement(textElement)
        : widget.text

    const options = getOptions(widget)
    const option = options?.find((option) => _.isEqual(option.value, value))
    const fontColor = option?.color
    const bgColor = option?.backgroundcolor
    const fullDisabled = disabled || widget?.disabled

    const onClick = useCallback(() => {
        triggerAction(widget?.action)
    }, [triggerAction, widget?.action])

    return (
        <Box
            bgcolor={bgColor ?? widget.bgcolor ?? ""}
            display="flex"
            alignItems="center"
            flexDirection="column"
            p={widget.padding}>
            {(bgColor || widget.bgcolor) && (
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        mr: "4px",
                        borderRadius: "50%",
                        backgroundColor:bgColor ?? widget.bgcolor,
                    }}
                />
            )}
            <StyledLabel
                onClick={widget?.action ? onClick : null}
                align={widget.textAlignment}
                disabled={fullDisabled}
                sx={{
                    width: "100%",
                    whiteSpace: "pre-line",
                    hyphens: "manual",
                    color: fontColor ?? (widget.color ?? ""),
                    wordBreak: widget.wordBreak,
                    ...errorState,
                    cursor: widget?.action ? "pointer" : "default",
                }}
                {...(fontSize && {fontSize: `${fontSize}px`})}
                {...(lineHeight && {lineHeight: `${lineHeight}px`})}
                component={widget.tag}
                variant={widget.tag}
                fontWeight={widget.fontWeight}>
                {displayValue}
                {widget.infotext && (
                    <InfoIcon
                        infoText={widget.infotext}
                        color={widget.color}
                    />
                )}
            </StyledLabel>
            {widget.subtext && (
                <Typography
                    align={widget.textAlignment}
                    sx={{
                        width: "100%",
                        whiteSpace: "pre-line",
                        color: widget.color ?? "",
                        ...errorState,
                    }}
                    fontWeight="600">
                    {widget.subtext}
                </Typography>
            )}
        </Box>
    )
}

export default LabelWidget
